import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import Sidebar from '../../components/Sidebar';
import useAuth from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const stripePromise = loadStripe('pk_test_51NDEMaHB8rLE0wX12LWngeUZjcnS2peDAoVfxzhasrTUX9EPo1Sef2XlbJgsjZjZegGsVM3obfYaWfiXGJtWGyDs00qtV3PXsA');

const RenewSubscriptionPage = () => {
  const [planns, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const stripe = useStripe();
  const elements = useElements();
  const user = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await fetch('https://api.theonlysale.com/api/get/plans'); 
        if (!response.ok) {
          throw new Error('Failed to fetch plans');
        }
        const data = await response.json();
        setPlans(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);
  
  const plans = [
    {
      title: planns[0]?.productName || "Basic Plan",
      price: planns[0] ? `${planns[0].formattedPrice} CHF` : "$19",
      description:
        "Ideal for those who love to shop and explore a wide range of products.",
      features: [
        "Access to all product listings",
        "Fast and secure checkout",
        "Personalized product recommendations",
        "Regular discounts and offers",
        "Basic customer support",
      ],
      productId:planns[0]?.product,
      priceId:planns[0]?.id,
      role:"user"
    },
    {
      title: planns[1]?.productName || "Marketplace Pro",
      price: planns[1] ? `${planns[1].formattedPrice} CHF` : "$49",
      description:
        "Perfect for users who want to take part in the community by buying and selling.",
      features: [
        "Everything in the Shopper plan, plus:",
        "Ability to list and sell second-hand items",
        "Enhanced visibility for your listings",
        "Priority customer support",
        "Access to exclusive seller analytics",
      ],
      productId:planns[1]?.product,
      priceId:planns[1]?.id,
      role:"premium"
    },
    {
      title: planns[2]?.productName || "Vendor Pro",
      price: planns[2] ? `${planns[2].formattedPrice} CHF` : "$149",
      description:
        "Designed for businesses that want to expand their reach by adding new products to the marketplace.",
      features: [
        "Everything in the Marketplace Pro plan, plus:",
        "Ability to add and sell new items",
        "Bulk listing options for large inventories",
        "Featured product placement in search results",
        "Comprehensive sales and inventory reports",
      ],
      productId:planns[2]?.product,
      priceId:planns[2]?.id,
      role:"business"
    },
  ];

  const handleRenewSubscription = async (priceId, productId) => {
    if (!stripe || !elements) {
      alert('Stripe.js has not loaded yet.');
      console.log('Stripe:', stripe, 'Elements:', elements);
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    const cardExpiryElement = elements.getElement(CardExpiryElement);
    const cardCvcElement = elements.getElement(CardCvcElement);

    if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
      console.error('Card input elements not found!');
      alert('Card input elements not found. Please try again.');
      return;
    }

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardNumberElement,
    });

    if (error) {
      console.error('Error creating payment method:', error);
      alert(error.message);
      return;
    }

    try {
      const response = await fetch('https://api.theonlysale.com/api/subscription/renew-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          selectedPlan: priceId,
          paymentMethodId: paymentMethod.id,
          role: plans.find(plan => plan.priceId === priceId).role,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to renew subscription');
      }

      const data = await response.json();
      alert('Subscription renewed successfully!');
      handleLogout(); // Log out after successful renewal
    } catch (err) {
      console.error('Error renewing subscription:', err);
      alert('Failed to renew subscription');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token'); 
    navigate('/'); 
  };

  if (loading) {
    return <div className="text-center p-4">Loading plans...</div>;
  }

  if (error) {
    return <div className="text-center text-red-500 p-4">Error: {error}</div>;
  }

  return (
    <div className="flex dark:bg-gray-700">
      <Sidebar />
      <div className="flex-grow p-6 ml-64">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Renew Your Subscription</h2>
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white">Card Number</label>
              <CardNumberElement className="border rounded-lg p-3 mt-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white">Expiration Date</label>
              <CardExpiryElement className="border rounded-lg p-3 mt-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white">CVC</label>
              <CardCvcElement className="border rounded-lg p-3 mt-2" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
          {plans.map((plan, index) => (
            <div key={index} className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">{plan.title}</h3>
              <p className="text-xl font-bold text-gray-700 dark:text-gray-300">{plan.price}</p>
              <p className="text-gray-600 dark:text-gray-400">{plan.description}</p>
              <ul className="mt-2 text-gray-600 dark:text-gray-400">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center">
                    <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 14l-4-4h8l-4 4z" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleRenewSubscription(plan.priceId, plan.productId)}
                className="w-full mt-4 bg-blue-600 text-white font-semibold py-2 rounded-md hover:bg-blue-700 transition-colors duration-300"
              >
                Renew {plan.title}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const WrappedRenewSubscriptionPage = () => (
  <Elements stripe={stripePromise}>
    <RenewSubscriptionPage />
  </Elements>
);

export default WrappedRenewSubscriptionPage;
